import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import {
  Wallet,
  TrendUp,
  TrendDown,
  Warning,
  Plus,
  Trash,
  ChartPie,
  ArrowUp,
  ArrowDown,
  Gauge
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CostControlPage() {
  const { token } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [costData, setCostData] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [budgetForm, setBudgetForm] = useState({
    category: '',
    account_id: '',
    amount: '',
    period: new Date().toISOString().slice(0, 7),
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      const [costRes, budgetRes, accountsRes] = await Promise.all([
        fetch(`${API}/api/analytics/cost-analysis?${params}`, { headers }),
        fetch(`${API}/api/analytics/budgets?period=${dateRange.start.slice(0, 7)}`, { headers }),
        fetch(`${API}/api/accounts?account_type=expense`, { headers })
      ]);
      
      if (costRes.ok) setCostData(await costRes.json());
      if (budgetRes.ok) setBudgets(await budgetRes.json());
      if (accountsRes.ok) setAccounts(await accountsRes.json());
    } catch (err) {
      toast.error('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/analytics/budgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...budgetForm,
          amount: parseFloat(budgetForm.amount) || 0
        })
      });
      
      if (res.ok) {
        toast.success('Budget created successfully');
        setShowBudgetModal(false);
        setBudgetForm({ category: '', account_id: '', amount: '', period: new Date().toISOString().slice(0, 7), notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create budget');
      }
    } catch (err) {
      toast.error('Failed to create budget');
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      const res = await fetch(`${API}/api/analytics/budgets/${budgetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Budget deleted');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete budget');
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="cost-control-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = costData?.summary || {};
  const costBreakdown = costData?.cost_breakdown || [];
  const overBudgetAlerts = costData?.over_budget_alerts || [];
  const recommendations = costData?.recommendations || [];

  // Prepare chart data for budget vs actual
  const budgetChartData = costBreakdown.slice(0, 8).map(item => ({
    name: item.account_name?.substring(0, 15) || 'Unknown',
    actual: item.actual,
    budget: item.budget || 0
  }));

  return (
    <div className="space-y-8" data-testid="cost-control-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Cost Control
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Budget management and expense analysis
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Input 
            type="date" 
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            className="w-[140px]"
          />
          <span className="text-muted-foreground">to</span>
          <Input 
            type="date" 
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            className="w-[140px]"
          />
          <Button onClick={fetchData} variant="outline" size="sm">Apply</Button>
          <Button onClick={() => setShowBudgetModal(true)} data-testid="create-budget-btn">
            <Plus size={18} className="mr-2" />
            Add Budget
          </Button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactile rounded-lg p-4 sm:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-500/10">
              <TrendDown size={20} className="text-red-600" weight="duotone" />
            </div>
            <div>
              <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                Total Actual
              </span>
              <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary.total_actual)}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-4 sm:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-500/10">
              <Wallet size={20} className="text-blue-600" weight="duotone" />
            </div>
            <div>
              <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                Total Budget
              </span>
              <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary.total_budget)}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-4 sm:p-6"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${(summary.total_variance || 0) >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {(summary.total_variance || 0) >= 0 ? 
                <ArrowDown size={20} className="text-emerald-600" weight="duotone" /> :
                <ArrowUp size={20} className="text-red-600" weight="duotone" />
              }
            </div>
            <div>
              <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                Variance
              </span>
              <p className={`text-xl sm:text-2xl font-heading font-semibold mt-0.5 ${(summary.total_variance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(Math.abs(summary.total_variance || 0))}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg p-4 sm:p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-500/10">
              <Gauge size={20} className="text-purple-600" weight="duotone" />
            </div>
            <div>
              <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                Utilization
              </span>
              <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                {summary.budget_utilization || 0}%
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* MoM Change & Over Budget Count */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Month-over-Month Change</p>
              <p className={`text-2xl font-bold ${summary.mom_change >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {summary.mom_change >= 0 ? '+' : ''}{summary.mom_change}%
              </p>
            </div>
            {summary.mom_change >= 0 ? 
              <TrendUp size={32} className="text-red-500 opacity-50" weight="duotone" /> :
              <TrendDown size={32} className="text-emerald-500 opacity-50" weight="duotone" />
            }
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {summary.mom_change >= 0 ? 'Expenses increased' : 'Expenses decreased'} compared to previous period
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className={`card-tactile rounded-lg p-6 ${summary.over_budget_count > 0 ? 'border-l-4 border-l-red-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Over Budget Categories</p>
              <p className={`text-2xl font-bold ${summary.over_budget_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {summary.over_budget_count}
              </p>
            </div>
            <Warning size={32} className={`${summary.over_budget_count > 0 ? 'text-red-500' : 'text-emerald-500'} opacity-50`} weight="duotone" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {summary.over_budget_count > 0 ? 'Categories exceeding budget' : 'All categories within budget'}
          </p>
        </motion.div>
      </div>

      {/* Budget vs Actual Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium mb-4">Budget vs Actual by Category</h3>
        <div className="h-[300px]">
          {budgetChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} className="text-xs" />
                <YAxis type="category" dataKey="name" width={100} className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [formatCurrency(value), '']}
                />
                <Legend />
                <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <ChartPie size={48} weight="duotone" className="mx-auto mb-2 opacity-50" />
                <p>No expense data for this period</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Cost Breakdown Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        <div className="p-4 border-b">
          <h3 className="font-heading text-lg font-medium">Cost Breakdown</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Budget</TableHead>
              <TableHead className="text-right hidden md:table-cell">Variance</TableHead>
              <TableHead className="text-right hidden lg:table-cell">Variance %</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {costBreakdown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No expense data for this period
                </TableCell>
              </TableRow>
            ) : (
              costBreakdown.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.account_name}</p>
                      <p className="text-xs text-muted-foreground">{item.account_code}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.actual)}</TableCell>
                  <TableCell className="text-right font-mono hidden sm:table-cell">
                    {item.budget ? formatCurrency(item.budget) : '-'}
                  </TableCell>
                  <TableCell className={`text-right font-mono hidden md:table-cell ${item.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {item.variance !== null ? formatCurrency(Math.abs(item.variance)) : '-'}
                  </TableCell>
                  <TableCell className={`text-right hidden lg:table-cell ${item.variance_percent !== null && item.variance_percent < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {item.variance_percent !== null ? `${item.variance_percent > 0 ? '+' : ''}${item.variance_percent}%` : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.budget ? (
                      <Badge className={item.status === 'over_budget' ? 'bg-red-500/10 text-red-600' : 'bg-emerald-500/10 text-emerald-600'}>
                        {item.status === 'over_budget' ? 'Over' : 'OK'}
                      </Badge>
                    ) : (
                      <Badge variant="outline">No Budget</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </motion.div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">Cost Reduction Recommendations</h3>
          <div className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Warning size={18} className="text-red-500" weight="fill" />
                  <span className="font-medium">{rec.category}</span>
                </div>
                <p className="text-sm text-muted-foreground">{rec.message}</p>
                <p className="text-sm text-red-600 mt-2">{rec.suggestion}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Budgets List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-heading text-lg font-medium">Budgets</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Budget</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Actual</TableHead>
              <TableHead className="text-right hidden md:table-cell">Variance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {budgets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No budgets set. Click "Add Budget" to create one.
                </TableCell>
              </TableRow>
            ) : (
              budgets.map((budget) => (
                <TableRow key={budget.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{budget.category}</p>
                      {budget.account_name && <p className="text-xs text-muted-foreground">{budget.account_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell>{budget.period}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(budget.amount)}</TableCell>
                  <TableCell className="text-right font-mono hidden sm:table-cell">{formatCurrency(budget.actual)}</TableCell>
                  <TableCell className={`text-right font-mono hidden md:table-cell ${budget.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(budget.variance)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteBudget(budget.id)}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </motion.div>

      {/* Budget Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Budget</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBudget} className="space-y-4">
            <div>
              <Label>Category Name</Label>
              <Input 
                value={budgetForm.category} 
                onChange={(e) => setBudgetForm({...budgetForm, category: e.target.value})} 
                placeholder="e.g., Office Supplies"
                required 
              />
            </div>
            <div>
              <Label>Expense Account (Optional)</Label>
              <Select value={budgetForm.account_id || "none"} onValueChange={(v) => setBudgetForm({...budgetForm, account_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.code} - {acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Budget Amount</Label>
              <Input 
                type="number" 
                value={budgetForm.amount} 
                onChange={(e) => setBudgetForm({...budgetForm, amount: e.target.value})} 
                placeholder="0.00"
                min="0"
                step="0.01"
                required 
              />
            </div>
            <div>
              <Label>Period</Label>
              <Input 
                type="month" 
                value={budgetForm.period} 
                onChange={(e) => setBudgetForm({...budgetForm, period: e.target.value})} 
                required 
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                value={budgetForm.notes} 
                onChange={(e) => setBudgetForm({...budgetForm, notes: e.target.value})} 
                placeholder="Optional notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowBudgetModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-budget-btn">Create Budget</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
